import { Injectable, OnDestroy, inject } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventType, EventMessage, AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import { AppStateHolder } from '../shared/app-state.holder';
import { environment } from '../../environments/environment';
import { DebugLoggerService } from './debug-logger.service';
import { AuthSessionService } from './auth-session.service';

@Injectable({ providedIn: 'root' })
export class MsalCookieSyncService implements OnDestroy {
  private static readonly LOG_PREFIX = '[AmSuite Auth]';

  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private appStateHolder = inject(AppStateHolder);
  private _debugLogger = inject(DebugLoggerService);
  private authSessionService = inject(AuthSessionService);
  private readonly destroy$ = new Subject<void>();

  startSync(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((msg: EventMessage) => {
        const result = msg.payload as AuthenticationResult;
        if (result?.accessToken) {
          this.syncTokenToCookie(result.accessToken);
          console.log(`${MsalCookieSyncService.LOG_PREFIX} token synced to cookie`, {
            event: msg.eventType,
            account: result.account?.username,
          });
          this._debugLogger.logAction('MsalCookieSync', 'Token synced to cookie', {
            event: msg.eventType,
            account: result.account?.username
          });
        }
      });

    // Log only — reauth è orchestrato da AuthSessionService (401 interceptor / guard).
    // Chiamare handleSessionExpired qui causava loop con eventi MSAL ripetuti.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
          msg.eventType === EventType.LOGIN_FAILURE
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((msg: EventMessage) => {
        const error = msg.error as { name?: string; message?: string };
        console.warn(`${MsalCookieSyncService.LOG_PREFIX} MSAL failure event (no auto-redirect)`, {
          eventType: msg.eventType,
          errorName: error?.name ?? null,
          errorMessage: error?.message ?? null,
          reauthInProgress: this.authSessionService.isReauthInProgress(),
        });
        this._debugLogger.logAction('MsalCookieSync', 'MSAL failure event logged', {
          event: msg.eventType,
          error: error?.message
        });
      });

    this.syncActiveAccountToken();

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.authSessionService.isReauthInProgress()) {
          this.syncActiveAccountToken();
        }
      });
  }

  private async syncActiveAccountToken(): Promise<void> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) return;

    try {
      const result = await this.msalService.instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'offline_access', environment.msalApiScope],
        account,
        forceRefresh: false,
      });

      if (result?.accessToken) {
        this.syncTokenToCookie(result.accessToken);
      }
    } catch {
      // Silent acquisition failed — AuthExpiredInterceptor gestirà il 401 sulle API.
    }
  }

  private syncTokenToCookie(accessToken: string): void {
    this.appStateHolder.setCookie('authType', 'b2c');
    this.appStateHolder.setCookie('authToken', accessToken);

    const account = this.msalService.instance.getActiveAccount();
    if (account?.username) {
      const domain = account.username.includes('@')
        ? account.username.split('@')[1]
        : '';
      if (domain) {
        this.appStateHolder.setCookie('authTypeDomain', domain);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
